import { useState } from "react";
import { toast } from "sonner";
import { Download, FileArchive, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { siErr } from "@/lib/sourcing";
import { fetchExportRows, saveCsv, saveResumeZip, saveXlsx } from "@/lib/talentExport";

// §75 Spreadsheet export (filtered or ticked) + one ZIP of the ticked people's resumes.
export const TalentActions = ({ qs, sel, items, canExport }) => {
  const [busy, setBusy] = useState("");
  const exp = async (kind) => {
    setBusy("export");
    try {
      const rows = await fetchExportRows(qs, sel);
      if (!rows.length) return toast.error("No one to export.");
      if (kind === "xlsx") await saveXlsx(rows); else saveCsv(rows);
      toast.success(`Exported ${rows.length} ${rows.length === 1 ? "person" : "people"}${sel.length ? " (ticked)" : " matching your filters"}.`);
    } catch (e) { toast.error(e.response?.status === 404 ? "Export isn't live on the backend yet (spec §75)." : e.response?.status === 403 ? "You don't have permission to export internal talent." : siErr(e)); } finally { setBusy(""); }
  };
  const zip = async () => {
    setBusy("zip");
    const id = toast.loading("Collecting resumes…");
    try {
      const people = items.filter((x) => sel.includes(x.id));
      const r = await saveResumeZip(people, (i, n) => toast.loading(`Collecting resume ${i} of ${n}…`, { id }));
      if (!r.added) toast.error("Couldn't download any of these resumes.", { id });
      else toast.success(`Downloaded ${r.added} resume${r.added > 1 ? "s" : ""} as one ZIP.${r.missing.length ? ` Missing: ${r.missing.join(", ")}.` : ""}`, { id });
    } catch { toast.error("Couldn't build the ZIP. Please try again.", { id }); } finally { setBusy(""); }
  };
  if (!canExport) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" disabled={!!busy} className="h-8 border-[#27272A] text-zinc-200" data-testid="talent-export">{busy === "export" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 text-[#D4AF37]" />}Export{sel.length ? ` (${sel.length})` : ""}</Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-[#27272A] bg-[#0f0f12]">
          <DropdownMenuItem onClick={() => exp("xlsx")} data-testid="talent-export-xlsx">Excel (.xlsx)</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exp("csv")} data-testid="talent-export-csv">CSV</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button size="sm" variant="outline" disabled={!sel.length || !!busy} onClick={zip} className="h-8 border-[#27272A] text-zinc-200" data-testid="talent-zip">{busy === "zip" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileArchive className="h-3.5 w-3.5 text-[#D4AF37]" />}Resumes ZIP{sel.length ? ` (${sel.length})` : ""}</Button>
    </>
  );
};
